import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWellnessSummaryData } from '../hooks/wellnessSummaryData';

// Icons for health categories
const ActivityIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#58A6FF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
  </svg>
);

const HeartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#58A6FF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
  </svg>
);

const SleepIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#58A6FF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    <text x="8" y="14" fontSize="8" fill="#58A6FF" fontFamily="Arial, sans-serif" fontWeight="bold">Zz</text>
  </svg>
);

const NutritionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#58A6FF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Apple left half outline */}
    <path d="M12 2.5C9.5 2.5 8 4 7.5 6C7 8 7 10 7.5 12L12 19.5" />
    {/* Apple right half outline */}
    <path d="M12 2.5C14.5 2.5 16 4 16.5 6C17 8 17 10 16.5 12L12 19.5" />
    {/* Core outline (oval shape in center) */}
    <ellipse cx="12" cy="11.5" rx="1.8" ry="2.8" />
    {/* Left seed (oval) */}
    <ellipse cx="10.8" cy="11" rx="0.7" ry="1" fill="#58A6FF" />
    {/* Right seed (oval) */}
    <ellipse cx="13.2" cy="11" rx="0.7" ry="1" fill="#58A6FF" />
    {/* Leaf at top */}
    <path d="M12 2.5L11.2 1.8C10.8 1.4 11.2 1.8 12 2.3L12.8 1.8C13.2 1.4 12.8 1.8 12 2.5z" />
    <path d="M11.2 1.8C11 1.6 11.1 1.9 11.3 2.1C11.5 2.3 11.7 2.2 11.2 1.8z" />
  </svg>
);

const RecoveryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#58A6FF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Person silhouette - head and shoulders */}
    <circle cx="12" cy="8" r="3" />
    <path d="M12 11v2" />
    <path d="M8 18h8" />
    <path d="M12 13v5" />
    {/* First recycling arrow - clockwise */}
    <path d="M3 12a9 9 0 0 1 9-9" />
    <path d="M3 12l3-3M3 12l3 3" />
    {/* Second recycling arrow - counter-clockwise */}
    <path d="M21 12a9 9 0 0 1-9 9" />
    <path d="M21 12l-3 3M21 12l-3-3" />
  </svg>
);

const BrowseHealthCategories = () => {
  const [patientId, setPatientId] = useState(null);
  
  // Get patient ID from localStorage on component mount
  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userInfo = JSON.parse(localStorage.getItem('user_info'));
      setPatientId(userInfo.di_id);
    }
  }, []);
  
  // Call the wellness summary API
  const { data: wellnessData, isLoading, error } = useWellnessSummaryData(patientId);
  
  // Log the data when it's available
  useEffect(() => {
    if (wellnessData) {
      console.log('Wellness Summary Data loaded:', wellnessData);
    }
  }, [wellnessData]);
  
  const healthCategories = [
    {
      id: 'activity',
      name: 'Activity',
      icon: <ActivityIcon />,
      description: 'Track your daily physical activities and exercise routines',
      color: 'bg-blue-500',
      path: '/health-categories/activity'
    },
    {
      id: 'heart',
      name: 'Heart',
      icon: <HeartIcon />,
      description: 'Monitor your heart rate, blood pressure, and cardiovascular health',
      color: 'bg-red-500',
      path: '/health-categories/heart'
    },
    {
      id: 'sleep',
      name: 'Sleep',
      icon: <SleepIcon />,
      description: 'Analyze your sleep patterns and quality',
      color: 'bg-purple-500',
      path: '/health-categories/sleep'
    },
    {
      id: 'nutrition',
      name: 'Nutrition',
      icon: <NutritionIcon />,
      description: 'Track your diet, calories, and nutritional intake',
      color: 'bg-green-500',
      path: '/health-categories/nutrition'
    },
    {
      id: 'recovery',
      name: 'Recovery',
      icon: <RecoveryIcon />,
      description: 'Monitor your recovery metrics and stress levels',
      color: 'bg-orange-500',
      path: '/health-categories/recovery'
    }
  ];

  return (
    <div className="min-h-screen pb-20 safe-area-top">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Health Pillars</h1>
          <p className="text-gray-400">Investigate, View trends, Gain insights</p>
        </div>

        {/* Health Categories List */}
        <div className="space-y-3">
          {healthCategories.map((category) => (
            <Link
              key={category.id}
              to={category.path}
              className="block"
            >
              <div className="bg-[#161B22] rounded-lg border border-[#30363D] px-4 py-4 flex items-center justify-between hover:bg-[#1f2937] transition-colors duration-200">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {category.icon}
                  </div>
                  <h3 className="text-lg font-medium text-white">{category.name}</h3>
                </div>
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </Link>
          ))}
        </div>

      </div>
    </div>
  );
};

export default BrowseHealthCategories; 